import { getAuthToken } from '../firebase/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Formats a date to YYYY-MM-DD string using local date methods to avoid timezone issues
 * @param {Date|string} date - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
const formatLocalDate = (date) => {
  if (!date) return '';
  
  // If it's already a string in YYYY-MM-DD format, return as is
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Convert to Date if it's not already
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date');
  }
  
  // Format as YYYY-MM-DD using local date to prevent timezone issues
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

/**
 * Deeply processes all date properties in an object
 * @param {Object} obj - The object to process
 * @returns {Object} Processed object with formatted dates
 */
const processDateProperties = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => processDateProperties(item));
  }
  
  // Create a new object to avoid mutating the original
  const processed = { ...obj };
  
  // Process each property
  for (const key in processed) {
    const value = processed[key];
    
    // If property is a date-like string or Date object
    if (key === 'date' || key === 'startDate' || key === 'endDate') {
      if (value instanceof Date || (typeof value === 'string' && !isNaN(new Date(value).getTime()))) {
        processed[key] = formatLocalDate(value);
      }
    } 
    // Recursively process nested objects and arrays
    else if (typeof value === 'object' && value !== null) {
      processed[key] = processDateProperties(value);
    }
  }
  
  return processed;
};

/**
 * Save a new itinerary
 * @param {Object} itineraryData - The itinerary data to save
 * @returns {Promise<Object>} The saved itinerary with an ID
 */
export const saveItinerary = async (itineraryData) => {
  try {
    const token = await getAuthToken();
    
    // Process all date properties throughout the entire object
    const formattedData = processDateProperties(itineraryData);
    
    const response = await fetch(`${API_BASE_URL}/itineraries/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(formattedData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      
      // Handle token-specific errors
      if (response.status === 402 && errorData.tokenRequired) {
        throw {
          message: 'Insufficient tokens to save itinerary',
          tokenRequired: true
        };
      }
      
      throw new Error(errorData.message || 'Failed to save itinerary');
    }
    
    const data = await response.json();
    
    // Include token balance in the response if available
    return {
      ...data.data,
      tokenBalance: data.tokenBalance
    };
  } catch (error) {
    console.error('Error saving itinerary:', error);
    
    // Pass through token errors
    if (error.tokenRequired) {
      throw error;
    }
    
    throw new Error('Failed to save itinerary');
  }
};

/**
 * Get all itineraries for the current user
 * @param {Object} filters - Optional filters
 * @returns {Promise<Array>} List of itineraries
 */
export const getUserItineraries = async (filters = {}) => {
  try {
    const token = await getAuthToken();
    
    // Build query string for filters
    const queryParams = new URLSearchParams();
    if (filters.destination) queryParams.append('destination', filters.destination);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.favorite === true) queryParams.append('favorite', 'true');
    
    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/itineraries${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch itineraries');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching itineraries:', error);
    throw new Error('Failed to fetch itineraries');
  }
};

/**
 * Get a specific itinerary by ID
 * @param {string} id - The itinerary ID
 * @returns {Promise<Object>} The itinerary
 */
export const getItineraryById = async (id) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/itineraries/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch itinerary');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error(`Error fetching itinerary ${id}:`, error);
    throw new Error('Failed to fetch itinerary');
  }
};

/**
 * Toggle favorite status of an itinerary
 * @param {string} id - The itinerary ID
 * @returns {Promise<void>}
 */
export const toggleFavorite = async (id) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/itineraries/${id}/favorite`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update favorite status');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error toggling favorite for itinerary ${id}:`, error);
    throw new Error('Failed to update favorite status');
  }
};

/**
 * Delete an itinerary
 * @param {string} id - The itinerary ID
 * @returns {Promise<void>}
 */
export const deleteItinerary = async (id) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/itineraries/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete itinerary');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error deleting itinerary ${id}:`, error);
    throw new Error('Failed to delete itinerary');
  }
};

/**
 * Update an existing itinerary
 * @param {string} id - The itinerary ID
 * @param {Object} itineraryData - The updated itinerary data
 * @returns {Promise<Object>} The updated itinerary
 */
export const updateItinerary = async (id, itineraryData) => {
  try {
    const token = await getAuthToken();
    
    // Process all date properties throughout the entire object
    const formattedData = processDateProperties(itineraryData);
    
    const response = await fetch(`${API_BASE_URL}/itineraries/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(formattedData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update itinerary');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error(`Error updating itinerary ${id}:`, error);
    throw new Error('Failed to update itinerary');
  }
}; 